import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  makeStyles,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Badge } from '@material-ui/core';
import clsx from 'clsx';

import { Can } from '../Can';
import { AuthContext } from '../../context/Auth/AuthContext';
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext';
import { i18n } from '../../translate/i18n';
import { menuGroupsConfig } from '../../utils/menuGroups';
import usePlans from '../../hooks/usePlans';

const useStyles = makeStyles((theme) => ({
  // ✅ ESTILOS PARA GRUPOS DE MENÚ - CORREGIDOS: Usa valores dinámicos del tema
  groupHeader: {
    borderRadius: '8px',
    margin: '4px 8px',
    backgroundColor: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease-in-out',
    '& .MuiListItemIcon-root': {
      minWidth: '40px',
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center',
    },
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      transform: 'translateX(2px)',
    },
  },
  
  // ✅ ESTILOS PARA GRUPOS DE MENÚ COLAPSADO
  groupHeaderCollapsed: {
    margin: '4px 4px',
    paddingLeft: '4px !important',
    paddingRight: '4px !important',
    justifyContent: 'center',
  },
  
  groupTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  groupIcon: {
    fontSize: '20px',
    color: theme.palette.text.secondary || theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // ✅ ESTILOS PARA ELEMENTOS DEL MENÚ - CORREGIDOS: Usa valores dinámicos del tema
  listItem: {
    borderRadius: '8px',
    margin: '2px 8px',
    backgroundColor: `${theme.mode === 'dark' ? '#374151' : theme.palette.background.paper} !important`,
    color: `${theme.palette.text.primary} !important`,
    transition: 'all 0.3s ease-in-out',
    '& .MuiListItemText-primary': {
      color: `${theme.palette.text.primary} !important`,
    },
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.text.secondary || theme.palette.text.primary} !important`,
      minWidth: '40px',
      justifyContent: 'center',
      '& svg': {
        fontSize: '20px',
      },
    },
    '&:hover': {
      backgroundColor: `${theme.mode === 'dark' ? '#4B5563' : '#E8F2FF'} !important`,
      transform: 'translateX(4px)',
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
        : '0 4px 12px rgba(59, 130, 246, 0.15)',
      '& .MuiListItemText-primary': {
        color: theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important',
        fontWeight: '500',
      },
      '& .MuiListItemIcon-root': {
        color: theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important',
      },
    },
  },
  
  // ✅ ESTILOS PARA ELEMENTOS ANIDADOS - CORREGIDOS: Usa valores dinámicos del tema
  nestedItem: {
    paddingLeft: theme.spacing(2),
    margin: '1px 4px',
    borderRadius: '6px',
    backgroundColor: `${theme.mode === 'dark' ? '#374151' : '#f9f9f9'} !important`,
    color: `${theme.palette.text.primary} !important`,
    '& .MuiListItemText-primary': {
      color: `${theme.palette.text.primary} !important`,
    },
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.text.secondary || theme.palette.text.primary} !important`,
      minWidth: '40px',
      justifyContent: 'center',
      '& svg': {
        fontSize: '20px',
      },
    },
    '&:hover': {
      backgroundColor: `${theme.mode === 'dark' ? '#4B5563' : '#F0F9FF'} !important`,
      transform: 'translateX(2px)',
      '& .MuiListItemText-primary': {
        color: theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important',
        fontWeight: '500',
      },
      '& .MuiListItemIcon-root': {
        color: theme.mode === 'dark' ? `${theme.palette.primary.light || '#60a5fa'} !important` : '#1E40AF !important',
      },
    },
  },
  
  // ✅ ESTILOS PARA ELEMENTOS ANIDADOS COLAPSADOS
  nestedItemCollapsed: {
    margin: '2px 4px',
    paddingLeft: '4px !important',
    paddingRight: '4px !important',
    justifyContent: 'center',
    '& .MuiListItemIcon-root': {
      marginRight: 0,
      minWidth: 'auto',
    },
  },
  
  // ✅ INDICADOR DE EXPANSIÓN - CORREGIDO: Usa valores dinámicos del tema
  expandIcon: {
    color: theme.palette.text.primary,
    transition: 'transform 0.3s ease-in-out',
  },
  
  expandedIcon: {
    transform: 'rotate(180deg)',
  },
}));

// ✅ COMPONENTE DE ELEMENTO DE MENÚ (REUTILIZADO)
function ListItemLink({ icon, primary, to, className, badge, drawerOpen = true, classes, ...props }) {
  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem 
        button 
        dense 
        component={renderLink} 
        className={clsx(className, !drawerOpen && classes?.nestedItemCollapsed)} 
        style={!drawerOpen ? {
          justifyContent: 'center',
          paddingLeft: '4px',
          paddingRight: '4px',
        } : undefined}
        {...props}
      >
        {icon ? (
          <ListItemIcon style={!drawerOpen ? { minWidth: 'auto', marginRight: 0 } : undefined}>
            {badge ? <Badge {...badge}>{icon}</Badge> : icon}
          </ListItemIcon>
        ) : null}
        {drawerOpen && <ListItemText primary={primary} />}
      </ListItem>
    </li>
  );
}

// ✅ COMPONENTE PRINCIPAL DE ACORDEÓN DE MENÚ
const AccordionMenu = ({ drawerClose, drawerOpen = true }) => {
  const classes = useStyles();
  const history = useHistory();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ✅ No utilizado por ahora
  
  const { user } = useContext(AuthContext);
  const { connectionWarning } = useContext(WhatsAppsContext);
  const { getPlanCompany } = usePlans();
  const isMounted = useRef(true);
  
  // ✅ ESTADO DE EXPANSIÓN DE GRUPOS
  const [expandedGroups, setExpandedGroups] = useState({
    whatsapp: true,      // ✅ Grupo principal expandido por defecto
    dashboard: false,
    gruposWA: false,
    mensajes: false,
    configuracion: false,
    utilidades: false,
  });

  // ✅ ESTADO PARA FORZAR RE-RENDERIZACIÓN CUANDO CAMBIE LA CONFIGURACIÓN DEL PLAN
  // eslint-disable-next-line no-unused-vars
  const [planConfigVersion, setPlanConfigVersion] = useState(0);

  // ✅ CARGAR CONFIGURACIÓN DEL PLAN DIRECTAMENTE (ya que MainListItems puede no estar renderizado)
  useEffect(() => {
    async function fetchPlanConfig() {
      if (isMounted.current && user?.companyId) {
        try {
          const planConfigs = await getPlanCompany(undefined, user.companyId);
          
          if (isMounted.current && planConfigs?.plan) {
            // ✅ ACTUALIZAR LOCALSTORAGE CON LA CONFIGURACIÓN DEL PLAN
            localStorage.setItem('showCampaigns', planConfigs.plan.useCampaigns ? 'true' : 'false');
            localStorage.setItem('showKanban', planConfigs.plan.useKanban ? 'true' : 'false');
            localStorage.setItem('showOpenAi', planConfigs.plan.useOpenAi ? 'true' : 'false');
            localStorage.setItem('showIntegrations', planConfigs.plan.useIntegrations ? 'true' : 'false');
            localStorage.setItem('showSchedules', planConfigs.plan.useSchedules ? 'true' : 'false');
            localStorage.setItem('showInternalChat', planConfigs.plan.useInternalChat ? 'true' : 'false');
            localStorage.setItem('showExternalApi', planConfigs.plan.useExternalApi ? 'true' : 'false');
            localStorage.setItem('showFiles', planConfigs.plan.useFiles ? 'true' : 'false');
            
            // ✅ FORZAR RE-RENDERIZACIÓN
            setPlanConfigVersion(prev => prev + 1);
          }
        } catch (error) {
          // ✅ NO MOSTRAR ERROR SI ES 401/403 - El interceptor de API lo manejará
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            // No hacer nada, el interceptor de API manejará el refresh token
          } else {
            console.error('Error cargando configuración del plan:', error);
          }
        }
      }
    }
    
    fetchPlanConfig();
    
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  // ✅ ESCUCHAR CAMBIOS EN LA CONFIGURACIÓN DEL PLAN PARA RE-RENDERIZAR
  useEffect(() => {
    // ✅ Escuchar evento personalizado cuando MainListItems actualiza la configuración (por si acaso)
    const handlePlanConfigUpdate = (event) => {
      setPlanConfigVersion(prev => prev + 1);
    };

    // ✅ Escuchar evento personalizado
    window.addEventListener('planConfigUpdated', handlePlanConfigUpdate);
    
    // ✅ También escuchar cambios en localStorage (para compatibilidad con otras pestañas)
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('show')) {
        setPlanConfigVersion(prev => prev + 1);
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('planConfigUpdated', handlePlanConfigUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // ✅ FUNCIÓN PARA TOGGLE DE GRUPOS O NAVEGAR SI TIENE RUTA
  const handleGroupToggle = (groupId) => {
    const group = menuGroupsConfig[groupId];
    
    // ✅ Si el grupo tiene una ruta directa y no tiene items, navegar directamente
    if (group.route && (!group.items || group.items.length === 0)) {
      history.push(group.route);
      if (drawerClose) drawerClose();
      return;
    }
    
    // ✅ Si tiene items, expandir/colapsar como antes
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // ✅ FUNCIÓN PARA VERIFICAR CONDICIONALES
  const checkConditional = (conditional, item) => {
    if (!conditional) return true;
    
    let result = false;
    switch (conditional) {
      case 'showKanban':
        result = localStorage.getItem('showKanban') === 'true';
        break;
      case 'showSchedules':
        result = localStorage.getItem('showSchedules') === 'true';
        break;
      case 'showInternalChat':
        result = localStorage.getItem('showInternalChat') === 'true';
        break;
      case 'showCampaigns':
        result = localStorage.getItem('showCampaigns') === 'true';
        break;
      case 'showOpenAi':
        result = localStorage.getItem('showOpenAi') === 'true';
        break;
      case 'showIntegrations':
        result = localStorage.getItem('showIntegrations') === 'true';
        break;
      case 'showExternalApi':
        result = localStorage.getItem('showExternalApi') === 'true';
        break;
      case 'showFiles':
        result = localStorage.getItem('showFiles') === 'true';
        break;
      case 'user.super':
        // ✅ Verificación estricta: solo true explícito
        result = user && user.super === true;
        break;
      default:
        result = true;
    }
    
    return result;
  };

  // ✅ FUNCIÓN PARA CREAR BADGE SI ES NECESARIO
  const createBadge = (item) => {
    if (item.route === '/connections' && connectionWarning) {
      return { badgeContent: "!", color: "error" };
    }
    if (item.route === '/chats') {
      return { color: "secondary", variant: "dot", invisible: false };
    }
    return null;
  };

  // ✅ RENDERIZADO DE GRUPO DE MENÚ
  const renderMenuGroup = (groupId) => {
    const group = menuGroupsConfig[groupId];
    if (!group) return null;

    // ✅ VERIFICAR PERMISOS A NIVEL DE GRUPO
    // ✅ Solo el grupo "whatsapp" (CHATS) es visible para usuarios regulares
    // ✅ Todos los demás grupos (dashboard, gruposWA, mensajes, configuracion, utilidades) son solo para admins
    const isAdminGroup = groupId !== 'whatsapp';
    if (isAdminGroup && user.profile !== 'admin') {
      return null; // ❌ OCULTAR GRUPOS ADMINISTRATIVOS PARA USERS
    }

    const isExpanded = expandedGroups[groupId];
    const hasVisibleItems = group.items && group.items.length > 0 && group.items.some(item => 
      checkConditional(item.conditional, item)
    );

    // ✅ Si el grupo tiene ruta directa y no tiene items visibles, mostrar solo el header clickeable
    if (group.route && !hasVisibleItems) {
      return (
        <React.Fragment key={groupId}>
          <ListItem
            button
            component={RouterLink}
            to={group.route}
            onClick={drawerClose}
            className={clsx(classes.groupHeader, !drawerOpen && classes.groupHeaderCollapsed)}
            style={{
              paddingLeft: drawerOpen ? undefined : '4px',
              paddingRight: drawerOpen ? undefined : '4px',
              justifyContent: drawerOpen ? 'flex-start' : 'center',
              margin: drawerOpen ? undefined : '4px 4px',
            }}
          >
            <ListItemIcon style={{ minWidth: drawerOpen ? '40px' : 'auto', marginRight: drawerOpen ? undefined : 0 }}>
              <span className={classes.groupIcon}>{group.icon}</span>
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary={
                  <Typography className={classes.groupTitle}>
                    {group.title}
                  </Typography>
                }
              />
            )}
          </ListItem>
          {drawerOpen && <Divider style={{ margin: '8px 0' }} />}
        </React.Fragment>
      );
    }

    if (!hasVisibleItems && !group.route) return null;

    return (
      <React.Fragment key={groupId}>
        {/* ✅ HEADER DEL GRUPO */}
        <ListItem
          button
          onClick={() => handleGroupToggle(groupId)}
          className={clsx(classes.groupHeader, !drawerOpen && classes.groupHeaderCollapsed)}
          style={{
            paddingLeft: drawerOpen ? undefined : '4px',
            paddingRight: drawerOpen ? undefined : '4px',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            margin: drawerOpen ? undefined : '4px 4px',
          }}
        >
          <ListItemIcon style={{ minWidth: drawerOpen ? '40px' : 'auto', marginRight: drawerOpen ? undefined : 0 }}>
            <span className={classes.groupIcon}>{group.icon}</span>
          </ListItemIcon>
          {drawerOpen && (
            <>
              <ListItemText
                primary={
                  <Typography className={classes.groupTitle}>
                    {group.title}
                  </Typography>
                }
              />
              {isExpanded ? (
                <ExpandLessIcon className={`${classes.expandIcon} ${classes.expandedIcon}`} />
              ) : (
                <ExpandMoreIcon className={classes.expandIcon} />
              )}
            </>
          )}
        </ListItem>

        {/* ✅ ELEMENTOS DEL GRUPO */}
        {hasVisibleItems && (
          <>
            {drawerOpen ? (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {group.items && group.items.map((item, index) => {
                    if (!checkConditional(item.conditional, item)) return null;

                    return (
                      <Can
                        key={`${groupId}-${index}`}
                        role={user.profile}
                        perform={item.permission}
                        yes={() => (
                          <ListItemLink
                            to={item.route}
                            primary={i18n.t(item.translationKey)}
                            icon={group.renderIcon(item.icon)}
                            className={classes.nestedItem}
                            badge={createBadge(item)}
                            onClick={drawerClose}
                            drawerOpen={drawerOpen}
                            classes={classes}
                          />
                        )}
                      />
                    );
                  })}
                </List>
              </Collapse>
            ) : (
              // ✅ Cuando el menú está colapsado, mostrar solo los iconos de los elementos
              <List component="div" disablePadding>
                {group.items && group.items.map((item, index) => {
                  if (!checkConditional(item.conditional, item)) return null;

                  return (
                    <Can
                      key={`${groupId}-${index}`}
                      role={user.profile}
                      perform={item.permission}
                      yes={() => (
                        <ListItemLink
                          to={item.route}
                          primary={i18n.t(item.translationKey)}
                          icon={group.renderIcon(item.icon)}
                          className={classes.nestedItem}
                          badge={createBadge(item)}
                          onClick={drawerClose}
                          drawerOpen={drawerOpen}
                          classes={classes}
                        />
                      )}
                    />
                  );
                })}
              </List>
            )}
          </>
        )}
        
        {/* ✅ DIVISOR ENTRE GRUPOS */}
        {drawerOpen && <Divider style={{ margin: '8px 0' }} />}
      </React.Fragment>
    );
  };

  return (
    <div onClick={drawerClose}>
      {/* ✅ RENDERIZAR TODOS LOS GRUPOS */}
      {Object.keys(menuGroupsConfig).map(renderMenuGroup)}
    </div>
  );
};

export default AccordionMenu;
